/**
 * Get a photo ready for upload: shrink and re-encode it in the browser.
 *
 * Phone photos are 3-12 MB (the API caps uploads at 10 MB and serves exactly
 * what it is given), and iPhone originals can be HEIC, which most browsers
 * can't show. Resizing here means "pick a photo from your phone" just works,
 * and the live site never serves an 8 MB hero image.
 *
 *  - SVG and GIF go up untouched (a GIF may be animated).
 *  - Anything the browser can decode is scaled so its longest edge is at most
 *    MAX_EDGE px and re-encoded as WebP (JPEG where the browser can't write
 *    WebP), honouring the EXIF rotation. Small files that need no resizing
 *    are left alone.
 *  - A HEIC/HEIF the browser can't decode gets a plain-language message
 *    instead of a bare "Unsupported file type".
 */

export const MAX_EDGE = 2400;
/** Files at or under this size, and already within MAX_EDGE, go up as-is. */
const LEAVE_ALONE_BYTES = 1.5 * 1024 * 1024;
const QUALITY = 0.86;

const EXT_TYPES: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  gif: 'image/gif',
  avif: 'image/avif',
  svg: 'image/svg+xml',
  heic: 'image/heic',
  heif: 'image/heif',
};

function extOf(name: string): string {
  return (name.split('.').pop() ?? '').toLowerCase();
}

/** MIME type of a picked file. Windows reports '' for .heic, so fall back to the extension. */
export function imageTypeOf(file: File): string {
  return file.type || EXT_TYPES[extOf(file.name)] || '';
}

export function isImageFile(file: File): boolean {
  return imageTypeOf(file).startsWith('image/');
}

function isHeic(file: File): boolean {
  return /hei[cf]/i.test(imageTypeOf(file)) || /^hei[cf]$/.test(extOf(file.name));
}

export const HEIC_MESSAGE =
  "This is an iPhone HEIC photo and this browser can't open it. In Photos, export it as a JPEG " +
  '(File → Export) or set the iPhone to Settings → Camera → Formats → Most Compatible. ' +
  'Safari on a Mac opens HEIC photos directly.';

function baseName(name: string): string {
  const dot = name.lastIndexOf('.');
  return (dot > 0 ? name.slice(0, dot) : name) || 'photo';
}

function toBlob(canvas: HTMLCanvasElement, type: string): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, QUALITY));
}

export async function prepareImage(file: File): Promise<File> {
  const type = imageTypeOf(file);
  if (type === 'image/svg+xml' || type === 'image/gif') return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
  } catch {
    if (isHeic(file)) throw new Error(HEIC_MESSAGE);
    // Not something this browser can decode — let the server judge it.
    return file;
  }

  try {
    const longest = Math.max(bitmap.width, bitmap.height);
    const scale = longest > MAX_EDGE ? MAX_EDGE / longest : 1;
    const decodable = type === 'image/jpeg' || type === 'image/png' || type === 'image/webp' || type === 'image/avif';
    if (scale === 1 && file.size <= LEAVE_ALONE_BYTES && decodable) return file;

    const w = Math.max(1, Math.round(bitmap.width * scale));
    const h = Math.max(1, Math.round(bitmap.height * scale));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);

    let out = await toBlob(canvas, 'image/webp');
    // Safari can't write WebP and quietly hands back a PNG. Keep a PNG
    // source as it is (it may be transparent); anything else becomes a JPEG.
    if (!out || out.type !== 'image/webp') {
      if (type === 'image/png' && scale === 1) return file;
      out = await toBlob(canvas, 'image/jpeg');
    }
    if (!out) return file;
    // Never make an unresized file bigger.
    if (scale === 1 && out.size >= file.size) return file;

    const ext = out.type === 'image/webp' ? 'webp' : 'jpg';
    return new File([out], `${baseName(file.name)}.${ext}`, { type: out.type });
  } finally {
    bitmap.close();
  }
}
